// AppShellHost — the full-screen boot/session failure gates.
//
// Extracted verbatim from AppShellHost.tsx (C1 decomposition). Pure projections
// of a typed `ExternalError` into the two terminal-error surfaces: the ONE
// legitimate full-screen failure (nothing loaded) and the keep-shell banner (a
// terminal error over a still-usable shell). Each takes the recovery callback
// (`onRetryNow`, bypassing backoff) via props and reads translations via
// `useLingui`; no component state, no I/O beyond the guaranteed reload action.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../components/Button/Button.js";
import {
    CopyErrorDetails,
    ErrorBanner,
} from "../../components/ErrorBanner/ErrorBanner.js";
import { Heading } from "../../components/Heading/Heading.js";
import { Text } from "../../components/Text/Text.js";
import { externalErrorId } from "../errorId.js";
import {
    type Display,
    type ExternalError,
    toDisplay,
} from "../externalError.js";
import {
    bootBannerOverlayStyle,
    bootFailureColumnStyle,
    centeredStyle,
} from "./styles.js";

type BootGateProps = {
    readonly error: ExternalError;
    readonly onRetryNow: () => void;
};

// A terminal boot/session error's ONE recovery — ALWAYS present, never a
// dead-end (audit D1). ReAuth reloads (re-runs boot; a hosted deploy then
// redirects to sign-in); a retriable transport fault retries NOW (no waiting
// on backoff); anything else reloads. The full-screen failure AND the
// keep-shell banner share this one guaranteed action.
const BootAction = ({
    display,
    onRetryNow,
}: {
    readonly display: Display;
    readonly onRetryNow: () => void;
}): ReactNode => {
    const { t } = useLingui();
    if (display.recovery === "ReAuth")
        return (
            <Button variant="primary" onClick={() => window.location.reload()}>
                {t`Sign in again`}
            </Button>
        );
    if (display.retriable)
        return (
            <Button variant="primary" onClick={onRetryNow}>
                {t`Try again`}
            </Button>
        );
    return (
        <Button variant="primary" onClick={() => window.location.reload()}>
            {t`Reload`}
        </Button>
    );
};

// The ONE legitimate full screen (audit D1): reached ONLY when there is
// genuinely nothing loaded (library === null). Calm + always recoverable —
// a labelled heading + human words from `toDisplay` (never a raw stack) +
// the guaranteed action. role=alert announces it; focus lands on the action.
export const BootFailure = ({
    error,
    onRetryNow,
}: BootGateProps): ReactNode => {
    const { t } = useLingui();
    const d = toDisplay(error);
    const errorId = externalErrorId(error);
    return (
        <div style={centeredStyle}>
            <div role="alert" style={bootFailureColumnStyle}>
                <Heading level={1} size="lg">
                    {t(d.title)}
                </Heading>
                {d.body !== undefined ? (
                    <Text tone="dim">{t(d.body)}</Text>
                ) : null}
                <Text tone="dim">{t`Nothing you made has been lost.`}</Text>
                <BootAction display={d} onRetryNow={onRetryNow} />
                <CopyErrorDetails errorId={errorId} />
            </div>
        </div>
    );
};

// The 404 gate (F48): an unrecognized URL is a real "page not found", not a
// silent bounce to a real view. Calm, one way out — a link to the start
// (`/` = new chat). role=alert announces it. Reuses the boot column so the 404
// reads as the same calm full-screen family as the boot gates.
export const NotFoundGate = ({
    onHome,
}: {
    readonly onHome: () => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div style={centeredStyle}>
            <div role="alert" style={bootFailureColumnStyle}>
                <Heading level={1} size="lg">
                    {t`Page not found`}
                </Heading>
                <Text tone="dim">
                    {t`That address doesn't lead anywhere in this workspace.`}
                </Text>
                <Button variant="primary" onClick={onHome}>
                    {t`Go to start`}
                </Button>
            </div>
        </div>
    );
};

// The keep-shell boot/session banner (audit D1): the SAME typed display, but
// floated over a still-usable shell instead of replacing it. Reached only
// when a terminal error arrives with a library already loaded.
export const BootBanner = ({ error, onRetryNow }: BootGateProps): ReactNode => {
    const { t } = useLingui();
    const d = toDisplay(error);
    return (
        <div style={bootBannerOverlayStyle}>
            <div style={{ pointerEvents: "auto" }}>
                <ErrorBanner
                    severity={d.severity}
                    title={t(d.title)}
                    body={d.body === undefined ? undefined : t(d.body)}
                    action={<BootAction display={d} onRetryNow={onRetryNow} />}
                    kept={t`Nothing has been lost — only this view is affected.`}
                    errorId={externalErrorId(error)}
                />
            </div>
        </div>
    );
};
